import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { billRefLabel } from '../../../../shared/contracts/bill-ref.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { TaxInvoice } from '../../../../shared/components/TaxInvoice';
import type { TaxInvoiceDoc } from '../../../../shared/contracts/tax-invoice.js';
import { ClearedBy } from './ClearedBy.js';
import { workOf, type CompletedWork } from '../../../../shared/contracts/completed-work.js';
import { loadBillDocument } from './billDocument.js';
import {
  BLANK_EXPENSE_ROW,
  calculationTotal,
  expenseEntriesOf,
  expenseRowComplete,
  expenseRowsOf,
  type ExpenseRow,
  type InvoiceCalculation,
} from '../../../../shared/contracts/other-expenses.js';

/** What is on screen for one piece of work: its calculation, then its bill. */
type Shown =
  | { readonly stage: 'calculation'; readonly work: CompletedWork; readonly calc: InvoiceCalculation; readonly entries: readonly ExpenseRow[] }
  | {
      readonly stage: 'bill';
      readonly work: CompletedWork;
      readonly calc: InvoiceCalculation;
      readonly entries: readonly ExpenseRow[];
      readonly doc: TaxInvoiceDoc;
      /** Still with the vendor, so it may be saved again and forwarded. */
      readonly withVendor: boolean;
    };

const key = (w: CompletedWork): string => w.jobRequestId ?? w.requestLineId ?? '';

/**
 * Completed Jobs (owner, 29 Oct): all of billing on one screen. "Raise
 * Invoice" - enabled once every level of the job's workflow has verified it -
 * first shows the calculation: the work at its agreed rate and, when its work
 * order allows them, the other expenses the vendor enters (head, amount,
 * details); otherwise they are 0. "Generate Bill" then shows the tax invoice
 * the system makes, with "Save" (the first save gives it its number; while it
 * is still with the vendor it can be raised and saved again), "Save and
 * Forward to Sales Accounts" and "Cancel".
 */
export function CompletedJobsPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const { decimal, money } = useBusinessNumber();
  const { day } = useBusinessDay();
  const [rows, setRows] = useState<readonly CompletedWork[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState<Shown | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await apiRequest<{ items: CompletedWork[] }>('/v1/vendor/billing/completed-jobs', { token });
      setRows(r.items);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  async function act(run: () => Promise<void>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await run();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const raiseInvoice = (w: CompletedWork) => act(async () => {
    const calc = await apiRequest<InvoiceCalculation>('/v1/vendor/billing/completed-jobs/invoice/calculation', {
      method: 'POST', token, body: workOf(w),
    });
    setShown({ stage: 'calculation', work: w, calc, entries: expenseRowsOf(calc) });
  });

  const generateBill = (s: Shown) => act(async () => {
    const doc = await apiRequest<TaxInvoiceDoc>('/v1/vendor/billing/completed-jobs/invoice/preview', {
      method: 'POST', token, body: { ...workOf(s.work), expenses: expenseEntriesOf(s.entries) },
    });
    setShown({ stage: 'bill', work: s.work, calc: s.calc, entries: s.entries, doc, withVendor: true });
  });

  const saveBill = (s: Shown, path: 'save' | 'forward') => act(async () => {
    const { claimId } = await apiRequest<{ claimId: string }>(`/v1/vendor/billing/completed-jobs/invoice/${path}`, {
      method: 'POST', token, body: { ...workOf(s.work), expenses: expenseEntriesOf(s.entries) },
    });
    const doc = await loadBillDocument(claimId, token);
    setShown({ stage: 'bill', work: s.work, calc: s.calc, entries: s.entries, doc, withVendor: path === 'save' });
    await load();
  });

  const setEntries = (entries: readonly ExpenseRow[]) =>
    setShown((s) => (s && s.stage === 'calculation' ? { ...s, entries } : s));

  return (
    <main aria-label="completed-jobs">
      <h1>{t('billing.completed.title')}</h1>
      <p>{t('billing.completed.intro')}</p>
      {error ? <p role="alert">{error}</p> : null}
      {rows === null ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="completed-jobs-empty">{t('billing.completed.empty')}</p>
      ) : (
        <DataTable
          rows={rows as CompletedWork[]}
          rowKey={key}
          rowTestId={(w) => `completed-${w.jobNumber}`}
          ariaLabel="completed-jobs-table"
          columns={[
            { key: 'job', header: t('billing.completed.colJob'), render: (w) => w.jobNumber },
            { key: 'branding', header: t('billing.completed.colBranding'), render: (w) => [w.brandingTypeName, w.brandingItemName].filter(Boolean).join(' / ') || '\u2014' },
            { key: 'qty', header: t('billing.completed.colQty'), render: (w) => `${decimal(w.billableQuantity)} ${unitLabel(w.uomCode)}` },
            { key: 'verification', header: t('billing.completed.colVerification'), render: (w) => (
              <>
                <div>{t(`billing.completed.status.${w.verificationStatus}`)}</div>
                <ClearedBy workflows={w.verifiedBy} />
              </>
            ) },
            { key: 'bill', header: t('billing.completed.colBill'), render: (w) => w.billedAs ? (
              <>
                {`${billRefLabel(w.billedAs.bill, day)} (${w.billedAs.status})`}
                {w.billedAs.reason ? (
                  <div data-testid={`bill-reason-${w.jobNumber}`} style={{ color: 'var(--color-warning)' }}>
                    {t('billing.completed.billReason', { reason: w.billedAs.reason })}
                  </div>
                ) : null}
              </>
            ) : '\u2014' },
            { key: 'action', header: t('billing.completed.colAction'), render: (w) => w.billedAs && !w.billedAs.canForward ? '\u2014' : (
              <Button type="button" variant="primary" disabled={!w.canRaiseBill || busy}
                      data-testid={`raise-bill-${w.jobNumber}`} onClick={() => void raiseInvoice(w)}>
                {t('billing.completed.raiseBill')}
              </Button>
            ) },
          ]}
        />
      )}
      {shown?.stage === 'calculation' ? (
        <section aria-label="invoice-calculation" style={{ marginTop: 24 }}>
          <h2>{t('billing.completed.calc.title')}</h2>
          <table aria-label="calculation-work">
            <thead>
              <tr>
                <th>{t('billing.completed.calc.colItem')}</th>
                <th>{t('billing.completed.calc.colQty')}</th>
                <th>{t('billing.completed.calc.colRate')}</th>
                <th>{t('billing.completed.calc.colAmount')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div>{shown.calc.work.jobRef}</div>
                  <div>{shown.calc.work.description}</div>
                </td>
                <td>{decimal(shown.calc.work.quantity)} {unitLabel(shown.calc.work.uomCode)}</td>
                <td>{money(shown.calc.work.rate)}</td>
                <td data-testid="calculation-work-amount">{money(shown.calc.work.amount)}</td>
              </tr>
            </tbody>
          </table>
          <h3>{t('billing.completed.calc.expenses')}</h3>
          {shown.calc.workOrder.otherExpensesAllowed ? (
            <>
              <p>{t('billing.completed.calc.allowed', { wo: shown.calc.workOrder.number })}</p>
              <table aria-label="calculation-expenses">
                <thead>
                  <tr>
                    <th>{t('billing.completed.calc.head')}</th>
                    <th>{t('billing.completed.calc.amount')}</th>
                    <th>{t('billing.completed.calc.details')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.entries.map((r, i) => {
                    const change = (patch: Partial<ExpenseRow>) =>
                      setEntries(shown.entries.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                    return (
                      <tr key={i} data-testid={`expense-row-${i}`}>
                        <td>
                          <select aria-label={`expense-head-${i}`} value={r.expenseTypeId} onChange={(e) => change({ expenseTypeId: e.target.value })}>
                            <option value="">{t('billing.completed.calc.pickHead')}</option>
                            {shown.calc.expenseHeads.map((h) => <option key={h.expenseTypeId} value={h.expenseTypeId}>{h.name}</option>)}
                          </select>
                        </td>
                        <td>
                          <input aria-label={`expense-amount-${i}`} type="number" min="0.01" step="0.01" value={r.amount}
                                 onChange={(e) => change({ amount: e.target.value })} />
                        </td>
                        <td>
                          <input aria-label={`expense-details-${i}`} maxLength={500} value={r.remarks}
                                 onChange={(e) => change({ remarks: e.target.value })} />
                        </td>
                        <td>
                          <Button type="button" variant="secondary" data-testid={`expense-remove-${i}`}
                                  onClick={() => setEntries(shown.entries.filter((_, j) => j !== i))}>
                            {t('billing.completed.calc.remove')}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Button type="button" variant="secondary" data-testid="expense-add"
                      onClick={() => setEntries([...shown.entries, BLANK_EXPENSE_ROW])}>
                {t('billing.completed.calc.addExpense')}
              </Button>
            </>
          ) : (
            <p data-testid="expenses-not-allowed">{t('billing.completed.calc.notAllowed', { wo: shown.calc.workOrder.number })}</p>
          )}
          <p>
            <strong>{t('billing.completed.calc.total')}: </strong>
            <span data-testid="calculation-total">{money(calculationTotal(shown.calc, expenseEntriesOf(shown.entries.filter(expenseRowComplete))))}</span>
          </p>
          <p>{t('billing.completed.calc.gstNote')}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button type="button" variant="primary" disabled={busy || !shown.entries.every(expenseRowComplete)}
                    data-testid="generate-bill" onClick={() => void generateBill(shown)}>
              {t('billing.completed.generateBill')}
            </Button>
            <Button type="button" variant="secondary" disabled={busy}
                    data-testid="calculation-cancel" onClick={() => setShown(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </section>
      ) : null}
      {shown?.stage === 'bill' ? (
        <section aria-label="raised-invoice" style={{ marginTop: 24 }}>
          {shown.doc.invoice.invoiceNumber ? null : (
            <p data-testid="invoice-not-saved">{t('billing.completed.notSaved')}</p>
          )}
          <TaxInvoice doc={shown.doc} />
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {shown.withVendor ? (
              <>
                <Button type="button" variant="secondary" disabled={busy}
                        data-testid="save-invoice" onClick={() => void saveBill(shown, 'save')}>
                  {t('billing.completed.saveInvoice')}
                </Button>
                <Button type="button" variant="primary" disabled={busy}
                        data-testid="forward-invoice" onClick={() => void saveBill(shown, 'forward')}>
                  {t('billing.completed.saveAndForward')}
                </Button>
              </>
            ) : null}
            <Button type="button" variant="secondary" disabled={busy}
                    data-testid="invoice-cancel" onClick={() => setShown(null)}>
              {t('common.cancel')}
            </Button>
          </div>
        </section>
      ) : null}
    </main>
  );
}
